<template>
  <section ref='mapRef' class='map' />
</template>

<script>
export default {
  name: 'StoragesMap'
};
</script>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import difference from 'lodash/difference';
import delay from 'delay';

import { buildMap } from '@/views/map';
import { isMobile } from '@/helpers/mobile_detect';

const props = defineProps({
  dataNode: { type: Node, required: true },
  allStorages: { type: Array, required: true },
  filteredStorages: { type: Array, required: true },
  activeStorage: { type: Object, required: false, default: undefined },
  mapType: { type: String, required: true, validator: value => ['ymap', 'gmap'].indexOf(value) !== -1 }, // eslint-disable-line max-len
  leadProps: { type: Object, required: true }
});
const emit = defineEmits(['bounds:change', 'balloon:open', 'balloon:hide']);

const mapRef = ref(null);
// NOTE: Do not store map in vue refs!! It breaks yandex map.destroy logic
let map = null;
// NOTE: Do not store marks in vue refs!! It breaks yandex marker.option logic
let marks = null;
let fitMapToMarkers = null;

onMounted(async () => {
  const mapData = await buildMap({
    node: mapRef.value,
    dataNode: props.dataNode,
    callbacks: {
      boundsChange,
      showBalloon: ({ markData: { id } }) => emit('balloon:open', id),
      hideBalloon: ({ markData: { id } }) => emit('balloon:hide', id)
    },
    mapType: props.mapType,
    isAutoCenter: !window.IS_BRAND_RU,
    isVueDataFlow: true,
    isNoBalloon: isMobile(),
    leadProps: props.leadProps
  });

  map = mapData.map;
  marks = mapData.marks;
  fitMapToMarkers = mapData.fitMapToMarkers;

  trackActiveStorage();
  // trackFilteredStorages(props.filteredStorages, props.allStorages, true);
});

onBeforeUnmount(() => {
  if (!map) { return; }

  map.destroy();
  map = null;
  marks = null;
});

function trackActiveStorage() {
  if (props.activeStorage) {
    const mark = marks.find(({ markData: { id } }) => id === props.activeStorage.id);

    if (!mark.isActive) {
      mark.executeShowBalloon();
    }
  } else {
    map.activeMarker?.executeHideBalloon();
  }
}

watch(() => props.activeStorage, trackActiveStorage);

function trackFilteredStorages(current, prev, isFirstRun) {
  if (!map) { return; }

  const toHide = difference(prev, current);
  const toShow = difference(current, prev);

  toHide.forEach(({ id }) => (
    marks
      .find(mark => mark.storageId === id)
      .hide()
  ));

  toShow.forEach(({ id }) => (
    marks
      .find(mark => mark.storageId === id)
      .show()
  ));

  if (fitMapToMarkers) {
    const storageIds = props.filteredStorages.map(({ id }) => id);
    const visibleMarks = marks.filter(({ storageId }) => storageIds.includes(storageId));

    emit('bounds:change', props.filteredStorages); // без этого мы сначала двигаем карту, а только потом обновляем отображемые склады
    fitMapToMarkers(map, visibleMarks, isFirstRun);
  }
}

function boundsChange(visibleMarks) {
  emit(
    'bounds:change',
    visibleMarks.map(mark => (
      props.allStorages.find(storage => storage.id === mark.storageId)
    ))
  );
}

watch(() => props.filteredStorages, (current, prev) => (
  trackFilteredStorages(current, prev, false)
));
</script>

<style scoped lang='sass'>
@import 'app/packs/stylesheets/functions/rem'
@import 'app/packs/stylesheets/mixins/responsive'

.map
  width: 100%

  +lte_ipad
    height: min(90vh, #{rem4(592px)})

  +gte_laptop
    height: 100%
</style>

<style lang='sass'>
section.map .ymaps3x0--controls
  z-index: 2 // override so .filter-options can be on the top
</style>
